import clsx from 'clsx';
import Btn from '../actions/Btn';
import { IconAngleDown } from '@icons';

export interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    className?: string;
    ariaLabel?: string;
}

type PageItem = number | 'ellipsis';

/** Compact page list: always includes first + last; inserts … when the range is large. */
export function getVisiblePageItems(currentPage: number, totalPages: number): PageItem[] {
    if (totalPages <= 0) return [];
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pageSet = new Set<number>([1, totalPages]);

    for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
        if (page >= 1 && page <= totalPages) {
            pageSet.add(page);
        }
    }

    if (currentPage <= 3) {
        pageSet.add(2);
        pageSet.add(3);
        pageSet.add(4);
    }

    if (currentPage >= totalPages - 2) {
        pageSet.add(totalPages - 3);
        pageSet.add(totalPages - 2);
        pageSet.add(totalPages - 1);
    }

    const sorted = [...pageSet].sort((a, b) => a - b);
    const items: PageItem[] = [];

    for (let index = 0; index < sorted.length; index += 1) {
        const page = sorted[index];
        if (index > 0 && page - sorted[index - 1] > 1) {
            items.push('ellipsis');
        }
        items.push(page);
    }

    return items;
}

const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    className,
    ariaLabel = 'Pagination',
}: PaginationProps) => {
    const pageItems = getVisiblePageItems(currentPage, totalPages);

    return (
        <nav className={clsx('pagination', className)} aria-label={ariaLabel}>
            <Btn
                variant="secondary-outlined"
                buttonSize="sm"
                buttonClasses="btn-action-icon btn-action-icon-previous"
                buttonIcon={<IconAngleDown />}
                buttonDisabled={currentPage <= 1}
                buttonOnClick={() => onPageChange(currentPage - 1)}
                ariaLabel="Previous page"
            />
            {pageItems.map((item, index) =>
                item === 'ellipsis' ? (
                    <span
                        key={`ellipsis-${index}`}
                        className="pagination-ellipsis"
                        aria-hidden="true"
                    >
                        …
                    </span>
                ) : (
                    <Btn
                        key={item}
                        variant="secondary-outlined"
                        buttonSize="sm"
                        buttonClasses={clsx(
                            'btn-action-icon',
                            'btn-action-icon-page',
                            item === currentPage && 'active-page',
                        )}
                        buttonText={item}
                        buttonOnClick={() => onPageChange(item)}
                        buttonAriaProps={item === currentPage ? { 'aria-current': 'page' } : undefined}
                        ariaLabel={`Page ${item}`}
                    />
                ),
            )}
            <Btn
                variant="secondary-outlined"
                buttonSize="sm"
                buttonClasses="btn-action-icon btn-action-icon-next"
                buttonIcon={<IconAngleDown />}
                buttonDisabled={currentPage >= totalPages}
                buttonOnClick={() => onPageChange(currentPage + 1)}
                ariaLabel="Next page"
            />
        </nav>
    );
};

export default Pagination;
